import { OpenInNew } from '@mui/icons-material';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
} from '@mui/material';

import { useMemo, useState } from 'react';
import CategoryChip from '~/components/CategoryChip';

import {
  CategoryFilterDropdown,
  CategoryFilterItem,
} from '~/components/CategoryFilterDropdown';
import { Link } from '~/components/Link';
import { LogoPoweredByLink } from '~/components/LogoPoweredByLink';
import ReportChip from '~/components/ReportChip';
import { BlurWrapper } from '~/components/SearchResultsTable/BlurWrapper';
import { Domains } from '~/types/domains';
export interface DomainTechnologiesTableProps {
  technologies: Domains.Technology[];
  blurred?: boolean;
}
export const DomainTechnologiesTable = ({
  technologies,
  blurred = false,
}: DomainTechnologiesTableProps) => {
  const [filteredCategories, setFilteredCategories] = useState<
    CategoryFilterItem[]
  >([]);
  const allCategories = useMemo(() => {
    return technologies.reduce<CategoryFilterItem[]>((map, technology) => {
      if (!map.find((item) => item.id === technology.categories_id)) {
        map.push({
          id: technology.categories_id,
          name: technology.category,
          industries_id: technology.industries_id,
          industries_name: technology.industry,
        });
      }
      return map;
    }, []);
  }, [technologies]);

  const filteredTechnologies = useMemo(() => {
    return (
      technologies
        .filter((technology) => {
          if (filteredCategories.length === 0) {
            return true;
          }
          return filteredCategories.some(
            (category) => category.id === technology.categories_id
          );
        })
        // sort first by industry, then category, then name
        .sort((a, b) => {
          if (a.industry !== b.industry) {
            return a.industry.localeCompare(b.industry);
          }
          if (a.category !== b.category) {
            return a.category.localeCompare(b.category);
          }
          return a.name.localeCompare(b.name);
        })
    );
  }, [technologies, filteredCategories]);

  const onChange = (value: CategoryFilterItem[]) => {
    setFilteredCategories(value);
  };

  return (
    <Paper>
      <TableContainer>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Technology</TableCell>
              <TableCell align="left">
                <CategoryFilterDropdown
                  value={filteredCategories}
                  categories={allCategories}
                  onChange={onChange}
                />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTechnologies.map((technology) => {
              return (
                <TableRow
                  key={technology.uuid}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell>
                    <BlurWrapper blurred={blurred}>
                      <ReportChip
                        item={{
                          value: technology.uuid,
                          type: 'report',
                          name: technology.name,
                        }}
                        onDelete={() => {}}
                        deleteIcon={<OpenInNew />}
                        component={(props) => (
                          <Link
                            href={technology.url}
                            target="_blank"
                            hideExternalIcon
                            {...props}
                          />
                        )}
                      />
                    </BlurWrapper>
                  </TableCell>
                  <TableCell align="left">
                    <BlurWrapper blurred={blurred}>
                      <CategoryChip
                        category={technology.category}
                        industry={technology.industry}
                        industries_id={technology.industries_id}
                      />
                    </BlurWrapper>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={2}>
                <LogoPoweredByLink />
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Paper>
  );
};
